import { useCallback, useEffect, useState } from 'react';

const BLOG_API_URL = 'https://api.webaiexcel.com/api/v1/dbagent/query';

export type BlogContent = {
  image_url: string;
  audio_url?: string;
  blog_text?: string;
};

export type BlogPost = {
  id: number;
  title: string;
  blog_type: string;
  content: BlogContent;
  created_at?: string | null;
  updated_at?: string | null;
};

export const formatSlug = (title: string) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const isPodcast = (post: BlogPost) => Boolean(post.content.audio_url);

export const postPath = (post: BlogPost) =>
  `/blogs/${isPodcast(post) ? 'podcast' : 'blog'}/${formatSlug(post.title)}`;

const postTimestamp = (post: BlogPost) => {
  const date = post.created_at ?? post.updated_at;
  return date ? new Date(date).getTime() : 0;
};

export const formatPostDate = (post: BlogPost) => {
  const date = post.created_at ?? post.updated_at;
  return date
    ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
};

// Newest first; ties broken by id so the order is stable.
const sortPosts = (posts: BlogPost[]) =>
  [...posts].sort((a, b) => postTimestamp(b) - postTimestamp(a) || b.id - a.id);

// Episode numbers count up from the oldest podcast.
export const episodeNumbers = (posts: BlogPost[]) => {
  const podcasts = posts.filter(isPodcast).reverse();
  return new Map(podcasts.map((post, index) => [post.id, index + 1]));
};

// Cached for the session so moving between the list and detail pages doesn't refetch.
let cachedPosts: BlogPost[] | null = null;

export const fetchBlogPosts = async (): Promise<BlogPost[]> => {
  if (cachedPosts) return cachedPosts;
  const response = await fetch(BLOG_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: 'SELECT id, title, blog_type, content, created_at, updated_at FROM cx_blog_posts',
      database: 'webaiexcel',
    }),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  cachedPosts = sortPosts(data.data || []);
  return cachedPosts;
};

export type LoadStatus = 'loading' | 'ready' | 'error';

export const useBlogPosts = () => {
  const [posts, setPosts] = useState<BlogPost[]>(cachedPosts ?? []);
  const [status, setStatus] = useState<LoadStatus>(cachedPosts ? 'ready' : 'loading');

  const load = useCallback(() => {
    setStatus('loading');
    fetchBlogPosts()
      .then((result) => {
        setPosts(result);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching blog posts:', error);
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    if (!cachedPosts) load();
  }, [load]);

  return { posts, status, reload: load };
};

// Detail pages get the post via router state when navigated from the list;
// on a direct visit or refresh they look it up by slug.
export const usePostBySlug = (slug: string | undefined, statePost?: BlogPost) => {
  const { posts, status, reload } = useBlogPosts();
  const post = posts.find((p) => formatSlug(p.title) === slug) ?? (statePost && formatSlug(statePost.title) === slug ? statePost : undefined);
  return { post, posts, status, reload };
};
